import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Check, Zap, Star, Lock, Flame, Megaphone, Sparkles, Crown, ArrowRight } from 'lucide-react';

const plans = [
  {
    id: 'basic',
    name: 'Basic',
    price: 10,
    description: 'Try the system with one niche',
    features: [
      '100 potential clients / month',
      '1 niche · 1 state',
      'Lead detail & contact info',
      '90-Day Challenge (read-only)',
      'Basic lead list',
    ],
    notIncluded: ['No AI tools', 'No CRM saving', 'No Marketing Studio'],
    cta: 'Get Started',
  },
  {
    id: 'starter',
    name: 'Starter',
    price: 15,
    description: 'For freelancers landing first clients',
    features: [
      '300 potential clients / month',
      '3 niches · 3 states',
      'Save to CRM · Status & notes',
      'Warm-Lead Indicator',
      '90-Day Client Acquisition Challenge',
      'AI Email Writer (5/day)',
      'AI Call Scripts (5/day)',
      'Marketing Studio (limited generations)',
    ],
    notIncluded: [],
    cta: 'Start with Starter',
  },
  {
    id: 'growth',
    name: 'Growth',
    price: 39,
    description: 'Built for serious outreach',
    features: [
      '2,000 potential clients / month',
      'Unlimited niches & states',
      'Advanced Warm-Lead scoring',
      'Unlimited 90-Day Challenges',
      'Unlimited AI Email & Call Scripts',
      'Full Marketing Studio (Ad Creatives + Playbooks)',
      'Website audit & feasibility scoring',
      'Performance analytics · CSV export',
    ],
    notIncluded: [],
    popular: true,
    cta: 'Get Growth',
  },
  {
    id: 'agency',
    name: 'Agency',
    price: 79,
    description: 'For agencies and high-volume teams',
    features: [
      '5,000+ potential clients / month',
      'Everything in Growth',
      'Bulk lead actions · Bulk AI generation',
      'Team access (2-5 seats)',
      'Priority Marketing Studio queue',
      'Priority support · Early features',
    ],
    notIncluded: [],
    premium: true,
    cta: 'Get Agency',
  },
];

const Pricing = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      {/* Header */}
      <header className="sticky top-0 z-50 glass-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-600 to-blue-600 flex items-center justify-center shadow-glow">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <span className="font-heading font-extrabold text-[17px]">Nexus<span className="gradient-text">Leads</span></span>
          </Link>
          <div className="flex items-center gap-3">
            {user ? (
              <button onClick={() => navigate('/dashboard')} className="btn-secondary text-sm" data-testid="nav-dashboard">Dashboard</button>
            ) : (
              <>
                <Link to="/login" className="text-sm font-medium text-slate-600 hover:text-slate-900 hidden sm:inline">Sign in</Link>
                <button onClick={() => navigate('/register')} className="btn-primary text-sm" data-testid="nav-register">
                  Get started <ArrowRight className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 hero-mesh" />
        <div className="relative max-w-5xl mx-auto px-4 py-16 sm:py-24 text-center">
          <span className="inline-flex items-center gap-1.5 badge-gradient mb-5">
            <Sparkles className="w-3 h-3" /> Pricing
          </span>
          <h1 className="font-heading text-4xl sm:text-6xl font-extrabold text-slate-900 leading-[1.05] tracking-tight">
            Pick your plan. <span className="gradient-text">Land your first paying client.</span>
          </h1>
          <p className="text-lg text-slate-600 mt-5 max-w-2xl mx-auto">
            Every plan includes the 90-Day Client Acquisition Challenge — a daily AI-built playbook for getting clients.
            Billing is currently unavailable in this preview.
          </p>
          <div className="flex items-center justify-center gap-6 mt-8 text-xs font-medium text-slate-500">
            <div className="flex items-center gap-1.5" data-testid="pricing-billing-unavailable"><Lock className="w-3.5 h-3.5" /> Checkout unavailable</div>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 -mt-8 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {plans.map((plan) => {
            const isAgency = plan.premium;
            const cardClass = plan.popular
              ? 'card-gradient-border'
              : isAgency
                ? 'card-base ring-2 ring-violet-200 shadow-elevated'
                : 'card-base card-hover';
            const inner = isAgency
              ? 'relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 to-violet-900 text-white p-6 flex flex-col'
              : 'p-6 flex flex-col';

            return (
              <div key={plan.id} className={`${cardClass} relative`}>
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                    <span className="badge-gradient">
                      <Star className="w-3 h-3" /> Most Popular
                    </span>
                  </div>
                )}
                <div className={inner}>
                  {isAgency && (
                    <div className="absolute inset-0 hero-mesh opacity-40 pointer-events-none" />
                  )}
                  <div className="relative">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className={`font-heading font-bold text-xl ${isAgency ? 'text-white' : 'text-slate-900'}`}>{plan.name}</h3>
                      {isAgency && <Crown className="w-4 h-4 text-amber-300" />}
                    </div>
                    <p className={`text-sm ${isAgency ? 'text-violet-100' : 'text-slate-500'}`}>{plan.description}</p>
                    <div className="mt-5 mb-6">
                      <span className={`font-heading font-extrabold ${isAgency ? 'text-white' : 'text-slate-900'} text-5xl tracking-tight`}>${plan.price}</span>
                      <span className={`${isAgency ? 'text-violet-200' : 'text-slate-500'} text-sm ml-1`}>/month</span>
                    </div>
                    <ul className="space-y-2.5 mb-6 flex-1">
                      {plan.features.map((feature) => (
                        <li key={feature} className={`flex items-start gap-2 text-sm ${isAgency ? 'text-violet-50' : 'text-slate-700'}`}>
                          <Check className={`w-4 h-4 ${isAgency ? 'text-emerald-300' : 'text-emerald-500'} mt-0.5 flex-shrink-0`} />
                          <span>{feature}</span>
                        </li>
                      ))}
                      {(plan.notIncluded || []).map((f) => (
                        <li key={f} className="flex items-start gap-2 text-sm text-slate-400">
                          <span className="w-4 h-4 flex items-center justify-center">✗</span>
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                    <button
                      disabled
                      data-testid={`select-plan-${plan.id}`}
                      className={
                        isAgency
                          ? 'inline-flex items-center justify-center gap-2 w-full bg-white/70 text-violet-900 font-bold rounded-lg px-5 py-2.5 cursor-not-allowed opacity-70'
                          : 'btn-secondary w-full cursor-not-allowed opacity-70'
                      }
                    >
                      Billing unavailable
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* What's included strip */}
        <div className="mt-12 grid sm:grid-cols-3 gap-4">
          {[
            { i: Flame, t: '90-Day Challenge', s: 'AI-built daily plan to land your first client.' },
            { i: Megaphone, t: 'Marketing Studio', s: 'Ad creatives + Playbooks with website audits.' },
            { i: Sparkles, t: 'AI Outreach', s: 'Email & call scripts that actually convert.' },
          ].map(({ i: Icon, t, s }) => (
            <div key={t} className="card-base p-5">
              <span className="icon-halo mb-3"><Icon className="w-4 h-4 text-violet-700" strokeWidth={2.5} /></span>
              <p className="font-heading font-bold text-slate-900">{t}</p>
              <p className="text-sm text-slate-500 mt-1">{s}</p>
            </div>
          ))}
        </div>

        {/* FAQ */}
        <div className="mt-16 max-w-3xl mx-auto">
          <h2 className="font-heading text-3xl font-extrabold text-slate-900 text-center mb-8">FAQ</h2>
          <div className="space-y-3">
            {[
              ['Can I cancel anytime?', 'Billing controls are temporarily unavailable in this preview.'],
              ['When can I purchase a plan?', 'Checkout is temporarily unavailable in this preview.'],
              ['Does every plan include the 90-Day Challenge?', "Yes. Every plan unlocks the AI-built 90-Day Client Acquisition Challenge."],
              ['Can I upgrade later?', 'Plan changes will be available again when billing is configured.'],
            ].map(([q, a]) => (
              <details key={q} className="card-base p-5 group">
                <summary className="flex items-center justify-between cursor-pointer">
                  <span className="font-semibold text-slate-900">{q}</span>
                  <span className="text-violet-600 group-open:rotate-45 transition-transform text-xl">+</span>
                </summary>
                <p className="text-sm text-slate-600 mt-3">{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
